import {
    ShareBaseDocument,
    ShareBaseFolder,
    ShareBaseItem,
    ShareBaseLibrary,
    pxItemTypeGuards
} from 'phoenix/domain';
export enum SortParameter {
    Name = 1,
    // Date created
    Date,
    Size,
    Type,
    ViewDate
}
export interface ISortContentFunction<T extends ShareBaseItem = ShareBaseItem> {
    (content:T[], sortType:string, asc:boolean): T[] 
}

/**
 * Get the name of the property that should be used for sorting
 * based on the sort parameter
 * 
 * @param {ShareBaseItem} item A single instance of the type of item to sort
 * @param {SortParameter} param The sort parameter.
 * @return {string} A property name that can be used for sorting objects.
 */
function getSortKey(item: ShareBaseItem, param: SortParameter) {
    if (!item) {
        return '';
    }

    if (pxItemTypeGuards.isShareBaseDocument(item)) {
        switch (param) {
            case SortParameter.Name:
                return 'DocumentName';
            case SortParameter.Date:
                return 'DateModified';
            case SortParameter.Size:
                return 'FileSize';
            case SortParameter.Type:
                return 'MimeType';
            case SortParameter.ViewDate:
                return 'RecentView';
            default:
                return '';
        }
    } else if (pxItemTypeGuards.isShareBaseFolder(item)) {
        switch (param) {
            case SortParameter.Name:
            case SortParameter.Size:
            case SortParameter.ViewDate:
            case SortParameter.Type: // For type and size, just sort folders by name
                return 'FolderName';
            case SortParameter.Date:
                return 'DateModified';
            default:
                return '';
        }
    } else {
        switch (param) {
            case SortParameter.Name:
            case SortParameter.Type:
            case SortParameter.Size:
            case SortParameter.ViewDate:
                return 'LibraryName';
            case SortParameter.Date:
                return 'DateCreated'
            default:
                return '';
        }
    }
};

/**
 * Switch sort function results based on the desired direction; i.e. ascending or descending
 * 
 * @param {boolean} asc Set to true to sort in ascending order
 * @returns {number} The sort function result based on the expected direction.
 */
function byDirection(asc: boolean) {
    return (result: number) => {
        if (!asc) {
            if (result > 0) {
                result = -1;
            } else if (result < 0) {
                result = 1;
            }
        }

        return result;
    }
}

/**
 * @ngdoc filter
 * @name sharebase.filter:sbSortContent
 * @kind function
 * 
 * @description
 * Sort a collection of @see ShareBaseItem objects based on provided
 * sort parameters.
 * 
 * @param {ShareBaseItem[]} content The content to sort
 * @param {string} sortType Determines which property the sorting should key off of.
 * @param {boolean} ascending Set to true to sort in ascending direction or false
 * to sort in descending direction
 * @returns {ShareBaseItem[]} The sorted content
 */
export function SortContentFilter(
    debugInfoEnabled:boolean, 
    gettextCatalog:ng.gettext.gettextCatalog
) {
    return function sortContent(content: ShareBaseItem[], sortType: string, asc: boolean) {
        if (!content) {
            return content;
        }

        let filter = getLocaleSort(gettextCatalog.getCurrentLanguage());
        let sortParam = SortParameter[sortType];
        if (!sortParam) {
            if (debugInfoEnabled) {
                // Alert devs invalid sort type
                console.error(`${sortType} is an invalid SortParameter`);
            }
            return content;
        }

        let libraries = content.filter(pxItemTypeGuards.isShareBaseLibrary) as ShareBaseLibrary[];
        let folders = content.filter(pxItemTypeGuards.isShareBaseFolder) as ShareBaseFolder[];
        let docs = content.filter(pxItemTypeGuards.isShareBaseDocument) as ShareBaseDocument[];
        let dirResult = byDirection(asc);

        if (libraries.length > 0) {
            // It is expected that libraries would never be mixed with a collection of
            // folders and documents, so we can treat the entire collection as a collection
            // of libraries

            // Libraries really can only be sorted by two parameters, name and date created
            // if sort parameter is date created, sort by date created, anything else, just
            // sort by name
            libraries.sort((lhs, rhs) => {
                let result;
                if (sortParam === SortParameter.Date) {
                    let lhsDate = new Date(lhs.DateCreated.toString());
                    let rhsDate = new Date(rhs.DateCreated.toString());
                    return dirResult(lhsDate.getTime() - rhsDate.getTime());
                } else {
                    result = filter(lhs.LibraryName, rhs.LibraryName);

                    // If not sorting by Name, always sort by Name ascending
                    // this will help call out that things such as size and type don't apply to libraries
                    return sortParam === SortParameter.Name ? dirResult(result) : result;
                }
            });

            return libraries;
        }

        let folderSortKey = getSortKey(folders[0], sortParam);
        let docSortKey = getSortKey(docs[0], sortParam);

        folders.sort((lhs, rhs) => {
            let result;
            if (sortParam === SortParameter.Date) {
                let lhsDate = new Date(lhs[folderSortKey]);
                let rhsDate = new Date(rhs[folderSortKey]);
                result = dirResult(lhsDate.getTime() - rhsDate.getTime())
            } else {
                result = filter(lhs.FolderName, rhs.FolderName);
                // If not sorting by Name, always sort by Name ascending
                // this will help call out that things such as size and type don't apply to folders
                return sortParam === SortParameter.Name ? dirResult(result) : result;
            }

            if (result === 0) {
                // Secondary sort by name if other comparator was equal
                result = dirResult(filter(lhs.FolderName, rhs.FolderName));
            }
            return result;
        });

        docs.sort((lhs, rhs) => {
            let result
            if (sortParam === SortParameter.Date || sortParam === SortParameter.ViewDate) {
                let lhsDate = new Date(lhs[docSortKey]);
                let rhsDate = new Date(rhs[docSortKey]);
                result = dirResult(lhsDate.getTime() - rhsDate.getTime());
            } else if (sortParam === SortParameter.Size) {
                result = dirResult(lhs[docSortKey] - rhs[docSortKey]);
            } else {
                result = dirResult(filter(lhs[docSortKey], rhs[docSortKey]));
            }

            if (result === 0) {
                // Secondary sort by name if other comparator was equal
                result = dirResult(filter(lhs.DocumentName, rhs.DocumentName));
            }
            return result;
        });

        return [...folders, ...docs];

        function getLocaleSort(lang:string) {
            switch (lang) {
                case 'ja':
                    return japaneseCompare;
                default:
                    return defaultCompare;
            }
            
            function defaultCompare(lhs:string, rhs:string) {
                return lhs.localeCompare(rhs);
            }

            function japaneseCompare(lhs:string, rhs:string) {
                let iconv = require('iconv-lite');

                // sjis encoded buffer must be converted to a hexadecimal string to be compared
                lhs = iconv.encode(lhs, 'shift-jis').toString('hex');
                rhs = iconv.encode(rhs, 'shift-jis').toString('hex');
                if (lhs === rhs) {
                    return 0;
                }
                return lhs < rhs ? -1 : 1;
            }
        }

    }
}

SortContentFilter.$inject = [
    'debugInfoEnabled',
    'gettextCatalog'
];


// WEBPACK FOOTER //
// ./src/sharebase/filters/sortContent.ts